import { type FormEvent, useState } from "react";
import { MdArrowBack, MdHelpOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { Button } from "components/button";
import { Select } from "components/form/select";
import globalStyles from "index.module.css";
import alexLiveSrc from "assets/alex-live-voyc-white.svg";
import { channelOptions, sampleRateOptions, useAudio } from "hooks/use-audio";
import { useSystemAudioContext } from "hooks/system-audio-context";
import { paths } from "config/paths";

import { AudioSettingsHelpDialog } from "./audio-settings-help-dialog";
import styles from "./index.module.css";

export const AudioSettings = () => {
  const navigate = useNavigate();
  const { onSaveConfig, devices, isLoading, audioConfig, onUpdateAudioConfig, error, isSupported } =
    useAudio();
  const {
    status: systemAudioStatus,
    surface: systemAudioSurface,
    connect: connectSystemAudio,
    disconnect: disconnectSystemAudio,
  } = useSystemAudioContext();
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSaveConfig();
    navigate(paths.call);
  };

  const selectOptions = devices.map((device) => ({
    value: device.deviceId,
    label: device.label,
  }));

  if (isLoading) {
    return (
      <div className={globalStyles.container}>
        <div className={globalStyles.formContainer}>
          <div className={styles.header}>
            <button
              aria-label="Back"
              className={styles.backButton}
              onClick={() => navigate(-1)}
              type="button"
            >
              <MdArrowBack color="#fff" size={24} />
            </button>
            <h1 className={styles.title}>Audio Settings</h1>
          </div>
          <p className={styles.loadingText}>Loading audio devices...</p>
        </div>
      </div>
    );
  }

  if (!isSupported || error) {
    return (
      <div className={globalStyles.container}>
        <div className={globalStyles.formContainer}>
          <div className={styles.header}>
            <button
              aria-label="Back"
              className={styles.backButton}
              onClick={() => navigate(-1)}
              type="button"
            >
              <MdArrowBack color="#fff" size={24} />
            </button>
            <h1 className={styles.title}>Audio Settings</h1>
          </div>
          <div className={styles.errorState}>
            <p className={styles.errorText}>
              {error || "Audio recording is not supported in this browser"}
            </p>
            <p className={styles.errorSubtext}>
              {isSupported
                ? "Please check your microphone permissions and try again"
                : "Please use a modern browser that supports Web Audio API"}
            </p>
            <Button onClick={() => window.location.reload()} type="button">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={globalStyles.container}>
      <form className={globalStyles.formContainer} onSubmit={handleSubmit}>
        <div className={styles.header}>
          <button
            aria-label="Back"
            className={styles.backButton}
            onClick={() => navigate(-1)}
            type="button"
          >
            <MdArrowBack color="#fff" size={24} />
          </button>
          <h1 className={styles.title}>Audio Settings</h1>
          <button
            aria-label="Help"
            className={styles.helpButton}
            onClick={() => setIsHelpDialogOpen(true)}
            type="button"
          >
            <MdHelpOutline color="#fff" size={24} />
          </button>
        </div>
        <p className={styles.description}>Configure your audio input device for call recording.</p>

        <div className={styles.formContent}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="input-device">
              Input Device
            </label>
            <Select
              className={styles.select}
              id="input-device"
              onChange={(value) => {
                const selectedDevice = devices.find((d) => d.deviceId === value);
                onUpdateAudioConfig({
                  inputDevice: selectedDevice || null,
                });
              }}
              options={selectOptions}
              placeholder="Select an input device"
              value={audioConfig.inputDevice?.deviceId ?? ""}
            />
            <div className={styles.helpText}>Select your microphone to capture agent audio.</div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="sample-rate">
              Sample Rate
            </label>
            <Select
              className={styles.select}
              id="sample-rate"
              onChange={(value) => {
                onUpdateAudioConfig({
                  sampleRate: value as number,
                });
              }}
              options={sampleRateOptions}
              value={audioConfig.sampleRate}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="channels">
              Audio Channels
            </label>
            <Select
              className={styles.select}
              id="channels"
              onChange={(value) => {
                onUpdateAudioConfig({
                  channels: value as number,
                });
              }}
              options={channelOptions}
              value={audioConfig.channels}
            />
          </div>

          {audioConfig.channels === 2 && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Customer audio
                {systemAudioStatus === "active" && (
                  <span className={styles.systemAudioBadge}>Active</span>
                )}
              </label>
              {systemAudioStatus === "active" ? (
                <>
                  <Button onClick={disconnectSystemAudio} type="button" variant="white-outline">
                    Disconnect
                  </Button>
                  <div className={styles.helpText}>
                    {systemAudioSurface === "tab"
                      ? "Capturing browser tab audio."
                      : "Capturing system audio. Your screen content is not sent anywhere."}
                  </div>
                </>
              ) : (
                <>
                  <Button onClick={() => void connectSystemAudio()} type="button" variant="white">
                    {systemAudioStatus === "ended" ? "Reconnect" : "Connect"}
                  </Button>
                  <div className={styles.helpText}>
                    {systemAudioStatus === "ended"
                      ? "System audio was stopped. Click Reconnect to resume."
                      : 'If your softphone is a desktop app, share your screen and check "Share system audio". If it runs in a browser tab, just select that tab. Your screen content is never captured or transmitted.'}
                  </div>
                </>
              )}
            </div>
          )}

          {audioConfig.channels === 2 && systemAudioStatus !== "active" && (
            <p className={styles.submitWarning}>
              Connect customer audio above before starting a call.
            </p>
          )}
          <Button
            className={styles.submitButton}
            disabled={!audioConfig.inputDevice}
            type="submit"
            variant="white"
          >
            Save Settings
          </Button>
        </div>
      </form>
      <AudioSettingsHelpDialog onOpenChange={setIsHelpDialogOpen} open={isHelpDialogOpen} />
      <div className={globalStyles.liveLogoContainer}>
        <img alt="Voyc" className={styles.logo} src={alexLiveSrc} />
      </div>
    </div>
  );
};
